/// <reference path="../../../../node_modules/monaco-editor/monaco.d.ts" />
import { Component, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { BaseComponent } from "../../shared/base.component";
import { ApiService } from "src/app/services";
import { LoadingScreenService } from "src/app/shared/loading-screen.service";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Router } from "@angular/router";
import { ModalService } from "src/app/shared/modal.service";
import { CellClickedEvent, ColDef, GridApi, GridOptions, ICellRendererParams, GridReadyEvent } from "ag-grid-community";
import { PfPlanFile } from "src/app/generated-models/PfPlanFile";
import { extendDefaultOptions } from "src/app/utility/gridHelper";
import { EditDeleteCellComponent, EditDeleteCellComponentParams } from "src/app/shared/grid/edit-delete-cell.component";
import { PreprocessDateTime } from "src/app/utility/view-field-mapping";
import { PlanEditorModalOptions, PlanJsonSchema } from "./modals/pf-plan-editor-modal/pf-plan-editor-modal.component";
import { NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ViewCellComponent, ViewCellComponentParams } from "src/app/shared/grid/view-cell.component";
import { IsServiceUser } from "src/app/utility/userprofile-helper";
import { PlanCommit } from "src/app/generated-models/PlanCommit";
import { PlanFileTypeEnum } from "src/app/shared/enums/plan-enums";
import { ActionCellComponent, ActionCellComponentParams } from "src/app/shared/grid/action-cell.component";
import { PlanDiffModalOptions } from "./modals/pf-plan-diff-modal/pf-plan-diff-modal.component";
import { PlanNewFileModalOptions } from "../plans/modals/plan-new-file-modal/plan-new-file-modal.component";

@Component({
  standalone: false,
  templateUrl: "./pf-plan-editor.component.html",
  styleUrls: ["../../styles.scss", "./pf.scss"],
})
export class PfPlanEditorComponent extends BaseComponent implements OnInit {
  editorModal: NgbModalRef;
  serviceTreeId: string;
  serviceTeam: string;
  latestVersion: number;
  pfPlanStatus: string;
  allPlanFiles: PfPlanFile[] = [];
  rowSourceData: PfPlanFile[] = [];
  gridOptions: GridOptions;
  gridApi: GridApi;
  initPageSize: number = 15;
  isReadonly: boolean;
  ifFilesChanged: boolean = false;
  canUndoOnboarding = false;
  rowMergedData: PfPlanFile[] = [];
  gridOptionsMerged: GridOptions;
  gridApiMerged: GridApi;
  originalpfPlanFile: PfPlanFile | null = null;
  hasSaveError: boolean = false;

  message: string;

  jsonSchema: PlanJsonSchema = { FullSchema: "", MergeSchema: "" };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private apiService: ApiService,
    protected modalService: ModalService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private title: Title
  ) {
    super();
  }

  async ngOnInit() {
    this.serviceTreeId = this.route.snapshot.params["serviceTreeId"];

    this.apiService
      .getUserProfile()
      .then((userProfile) => {
        if (userProfile) {
          this.isReadonly = !(userProfile.IsAdmin || userProfile.IsPfAdmin || IsServiceUser(userProfile, this.serviceTreeId));
          if (this.isReadonly) {
            const viewCellComponentParams: ViewCellComponentParams<PfPlanFile> = {
              viewFunc: this.viewPfPlanFile.bind(this),
            };
            const columnDefs = [];
            this.gridOptions.columnDefs.map((map) => {
              if (map.headerName === "Action") {
                columnDefs.push({
                  headerName: map.headerName,
                  sortable: false,
                  suppressMenu: true,
                  cellRenderer: ViewCellComponent,
                  cellRendererParams: viewCellComponentParams,
                });
              } else {
                columnDefs.push(map);
              }
            });

            this.gridApi.setGridOption('columnDefs', columnDefs);
          }
          // If user is Admin or PfAdmin, then undo onboarding button is visible.
          if (userProfile.IsAdmin || userProfile.IsPfAdmin) {
            this.canUndoOnboarding = true;
          }
        } else {
          this.notificationService.error("user profile returns null, no data was loaded.");
        }
      })
      .catch((error) => {
        this.notificationService.error(`unable to get user profile, error: ${error.message}`);
      });

    this.apiService.getPfPlanLatestVersion(this.serviceTreeId).subscribe(
      (response) => {
        this.latestVersion = response.Version;
        this.pfPlanStatus = response.Status;
        this.serviceTeam = response.ServiceTeam;
        this.title.setTitle(`${this.route.snapshot.data.title} - ${this.serviceTeam}`);
      },
      (e: unknown) => {
        this.notificationService.error(`Failed to find pf plan for ServiceTree Id ${this.serviceTreeId}. Error: ${e.toString()}`);
      }
    );

    this.apiService.getPfPlanJsonSchema().subscribe(
      (response) => {
        this.jsonSchema.FullSchema = response.FullSchema;
        this.jsonSchema.MergeSchema = response.MergeSchema;
        setTimeout(function () {
          if (!response) {
            this.notificationService.error("Editor json schema returns null, no data was loaded.");
          }
        }, 200);
      },
      (e: unknown) => {
        this.notificationService.error(`Editor json schema returns null, no data was loaded. Error: ${e.toString()}`);
      }
    );

    const planFilesColumnDefinition: ColDef[] = [
      {
        headerName: "Action",
        sortable: false,
        suppressMenu: true,
        cellRendererSelector: (params: ICellRendererParams) => {
          if (params.data?.PlanFileStatus !== "Deleted") {
            return {
              component: EditDeleteCellComponent,
              params: {
                editFunc: this.editPfPlanFile.bind(this),
                deleteFunc: this.deletePfPlanFile.bind(this),
              } as EditDeleteCellComponentParams<PfPlanFile>,
            };
          }

          return undefined;
        },
      },
      {
        headerName: "Name",
        field: "FileName",
      },
      {
        headerName: "Last Modified",
        field: "LastModified",
        cellRenderer: (params) => PreprocessDateTime(params.value),
      },
      {
        headerName: "File Status",
        field: "PlanFileStatus",
        cellRenderer: ActionCellComponent,
        cellRendererParams: {
          actionFunc: this.diffPfPlanFile.bind(this),
        } as ActionCellComponentParams<PfPlanFile>,
      },
      {
        headerName: "Last Modified By",
        field: "LastModifiedBy",
      },
    ];

    const planFilesMergedColumnDefinition: ColDef[] = [
      {
        headerName: "Action",
        colId: "action",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        cellRendererSelector: (params: ICellRendererParams) => {
          if (params.data?.PlanFileStatus !== "Deleted") {
            return {
              component: () => `
                <button type="button" class="btn btn-link p-0 d-inline-flex align-items-center justify-content-center" style="line-height: 1;">
                  <i class="fas fa-sm fa-code" style="margin-right: 0;"></i>
                </button>`,
            };
          }

          return undefined;
        },
      },
      {
        headerName: "Name",
        field: "FileName",
      },
      {
        headerName: "Last Modified",
        field: "LastModified",
        cellRenderer: (params) => PreprocessDateTime(params.value),
      },
      {
        headerName: "File Status",
        field: "PlanFileStatus",
        cellRenderer: ActionCellComponent,
        cellRendererParams: {
          actionFunc: this.diffPfPlanFile.bind(this),
        } as ActionCellComponentParams<PfPlanFile>,
      },
      {
        headerName: "File Type",
        field: "PlanFileType",
      },
    ];

    this.gridOptions = extendDefaultOptions({
      columnDefs: planFilesColumnDefinition,
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      sideBar: null,
      rowClassRules: {
        "ag-row-added": (params) => {
          return params.data.PlanFileStatus === "Added";
        },
        "ag-row-modified": (params) => {
          return params.data.PlanFileStatus === "Modified";
        },
        "ag-row-deleted": (params) => {
          return params.data.PlanFileStatus === "Deleted";
        },
      },
    });

    this.gridOptionsMerged = extendDefaultOptions({
      onGridReady: (params: GridReadyEvent) => { this.gridApiMerged = params.api; },
      onCellClicked: this.onCellClicked.bind(this),
      columnDefs: planFilesMergedColumnDefinition,
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      sideBar: null,
      rowClassRules: {
        "ag-row-added": (params) => {
          return params.data.PlanFileStatus === "Added";
        },
        "ag-row-modified": (params) => {
          return params.data.PlanFileStatus === "Modified";
        },
        "ag-row-deleted": (params) => {
          return params.data.PlanFileStatus === "Deleted";
        },
      },
    });

    this.getPfPlanFiles();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  protected async onCellClicked(event: CellClickedEvent) {
    if (event.colDef.colId === "action") {
      this.viewPfPlanFile(event.data as PfPlanFile);
    }
  }

  getPfPlanFiles() {
    this.loadingService.setLoading(true);

    this.apiService.getLatestPfPlanFiles(this.serviceTreeId).subscribe(
      (response) => {
        const original: PfPlanFile[] = [];
        const generated: PfPlanFile[] = [];
        response.forEach((plan) => {
          if (plan.FileName === "default.json") {
            original.push(plan);
            generated.push(plan);
          } else if (plan.FileName.includes("override")) {
            original.push(plan);
          } else {
            if (plan.PlanFileType === PlanFileTypeEnum.Generated) {
              generated.push(plan);
            } else {
              original.push(plan);
              generated.push(plan);
            }
          }
        });
        this.rowSourceData = original;
        this.rowMergedData = generated;
        this.allPlanFiles = response;
        this.ifFilesChanged = this.rowSourceData.filter((row) => row.PlanFileStatus != "NoChange").length > 0;
        this.loadingService.setLoading(false);
        setTimeout(() => {
          this.gridApi.autoSizeAllColumns();
          this.gridApiMerged.autoSizeAllColumns();
        }, 100);
      },
      (e: unknown) => {
        this.rowSourceData = [];
        this.rowMergedData = [];
        this.allPlanFiles = [];
        this.loadingService.setLoading(false);
        this.notificationService.error(e.toString());
      }
    );
  }

  async viewPfPlanFile(pfPlanFile: PfPlanFile) {
    this.showPfPlanEditorDialog(pfPlanFile, true);
  }

  editPfPlanFile(pfPlanFile: PfPlanFile) {
    // If there was a save error previously, reload the original plan file for editing
    if (this.hasSaveError && this.originalpfPlanFile) {
      this.showPfPlanEditorDialog(this.originalpfPlanFile, false);
    } else {
      this.showPfPlanEditorDialog(pfPlanFile);
    }
  }

  async diffPfPlanFile(pfPlanFile: PfPlanFile) {
    await this.showPlanDiffDialog(pfPlanFile);
  }

  async deletePfPlanFile(pfPlanFile: PfPlanFile) {
    try {
      await this.modalService.confirmationModal(`Are you sure to delete ${pfPlanFile.FileName}?`);
    } catch {
      // For the model dialog dimiss
      return;
    }

    this.loadingService.setLoading(true);

    this.apiService.deletePfPlanFile(this.serviceTreeId, pfPlanFile.FileName).subscribe(
      () => {
        this.message = `Plan file has been deleted successfully.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (error) => {
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
      },
      () => {
        this.getPfPlanFiles();
      }
    );
  }

  async showPfPlanNewFileDialog() {
    const cloudOverrideExist = this.rowSourceData.some((file) => file.FileName.includes("cloud"));
    const regionTypeOverrideExist = this.rowSourceData.some((file) => file.FileName.includes("type"));
    const overrideNameList = this.rowSourceData.map((file) => file.FileName.split(".")[0]);
    const options: PlanNewFileModalOptions = {
      cloudOverrideExist: cloudOverrideExist,
      regionTypeOverrideExist: regionTypeOverrideExist,
      overrideNameList: overrideNameList,
    };
    const fileName: string = await this.modalService.planNewFileModal(options);

    if (fileName) {
      const pfPlanFile: PfPlanFile = this.rowSourceData.find((p) => p.FileName === `${fileName}.json`);
      if (pfPlanFile) {
        this.showPfPlanEditorDialog(pfPlanFile);
      } else {
        const emptyPfPlanFile = {
          FileName: `${fileName}.json`,
          Content: "{}",
          Format: "json",
        } as PfPlanFile;

        if (fileName.includes("override")) {
          // partial mode
          const cloudOrRegionName = fileName.split(".")[0];
          this.checkOverrideExist(cloudOrRegionName, emptyPfPlanFile);
        } else {
          // fully mode
          const planTemplate = await this.apiService.getPfPlanFileTemplate(this.serviceTreeId).toPromise();
          const cloudOrRegionName = fileName;
          emptyPfPlanFile.Content = planTemplate.Content;
          this.checkOverrideExist(cloudOrRegionName, emptyPfPlanFile);
        }
      }
    }
  }

  checkOverrideExist(cloudOrRegionName: string, emptyPfPlanFile: PfPlanFile) {
    const pfPlanFile: PfPlanFile = this.allPlanFiles.find((p) => p.FileName === `${cloudOrRegionName}.json`);
    if (pfPlanFile) {
      this.notificationService.error(`An override blueprint file for ${cloudOrRegionName} alread exists.`);
    } else {
      this.showPfPlanEditorDialog(emptyPfPlanFile);
    }
  }

  showPfPlanEditorDialog(pfPlanFile: PfPlanFile, isReadonly: boolean = false) {

    // Save original pfPlan file content when editor opens,Use JSON deep copy to avoid mutation
    this.originalpfPlanFile = JSON.parse(JSON.stringify(pfPlanFile));

    const options: PlanEditorModalOptions = {
      model: pfPlanFile,
      save: this.savePfPlanFile.bind(this),
      isReadonly: isReadonly,
      schema: pfPlanFile.FileName.includes("override") ? this.jsonSchema.MergeSchema : this.jsonSchema.FullSchema,
    };
    this.editorModal = this.modalService.pfPlanEditorModal(options);
  }

  async showPlanDiffDialog(pfPlanFile: PfPlanFile) {
    const options: PlanDiffModalOptions = {
      model: pfPlanFile,
      isReadonly: true,
    };
    this.editorModal = this.modalService.planDiffModal(options);
  }

  savePfPlanFile(pfPlanFile: PfPlanFile) {
    this.loadingService.setLoading(true);

    this.apiService.savePfPlanFile(this.serviceTreeId, pfPlanFile).subscribe(
      () => {
        this.message = `PF capacity blueprint file has been saved successfully.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
        this.editorModal.close();
        this.hasSaveError = false;
      },
      (error) => {
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
        this.hasSaveError = true;
      },
      () => {
        this.getPfPlanFiles();
      }
    );
  }

  async undoOnboarding() {
    try {
      await this.modalService.confirmationModal(
        `Are you sure to undo onboarding for service ${this.serviceTreeId}? This will delete all blueprint files and service owner need to re-onboard again.`
      );
    } catch {
      // For the model dialog dimiss
      return;
    }

    this.loadingService.setLoading(true);

    this.apiService.undoOnboardPfPlan(this.serviceTreeId).subscribe(
      () => {
        this.message = `All blueprint files for service ${this.serviceTreeId} are removed.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);

        // delay and redirect to pf plan list page
        setTimeout(() => {
          this.router.navigateByUrl("/pf/all");
        }, 1000);
      },
      (error) => {
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
      }
    );
  }

  async showPlanHistoryDialog() {
    await this.modalService.pfPlanHistoryModal(this.serviceTreeId);
  }

  async showCommitPlanDialog() {
    try {
      const comment = await this.modalService.planCommitModal();
      await this.commitPfPlan(comment);
    } catch {
      // For the model dialog dismiss
    }
  }

  async commitPfPlan(comment: string) {
    this.loadingService.setLoading(true);

    const payload: PlanCommit = {
      Comment: comment,
    };

    this.apiService.commitPfPlan(this.serviceTreeId, payload).subscribe(
      async (result) => {
        this.message = `Successfully committed the pf plan version(<b>${result.Version}</b>)</br>`;

        this.loadingService.setLoading(false);
        await this.modalService.planCommitResponseModal(this.message); // todo
        this.getPfPlanFiles();
      },
      (error) => {
        if (!error) {
          error = `Failed to commit pf plan.`;
        }
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
      }
    );
  }
}
